import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import AttendancePage from "./pages/AttendancePage";
import AssetPage from "./pages/AssetPage";
import ClientPage from "./pages/ClientPage";
import DeliverablePage from "./pages/DeliverablePage";
import MasterSheetPage from "./pages/MasterSheetPage";
import ShootPage from "./pages/ShootPage";
import ProductionPage from "./pages/ProductionPage";
import ProductionCastPage from "./pages/ProductionCastPage";
import ExecutionBoardPage from "./pages/ExecutionBoardPage";
import PerformanceBoardPage from "./pages/PerformanceBoardPage";
import BusinessDevPage from "./pages/BusinessDevPage";
import PayrollPage from "./pages/PayrollPage";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import ApprovalsPage from "./pages/ApprovalsPage";
import TalentPage from "./pages/TalentPage";
import InvoicesPage from "./pages/InvoicesPage";
import FinancePage from "./pages/FinancePage";
import PunchTrackerPage from "./pages/PunchTrackerPage";
import EmployeeTrackerPage from "./pages/EmployeeTrackerPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import AuditLogsPage from "./pages/AuditLogsPage";
import DailyBriefPage from "./pages/DailyBriefPage";
import { Toaster } from "./components/ui/sonner";
import "./index.css";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/employee" replace />;
  return children;
}

function PermRoute({ children, tracker }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return children;
  const level = user.effective_permissions?.[tracker] || 'none';
  if (level !== 'none') return children;
  return <Navigate to="/employee" replace />;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "employee") return <Navigate to="/employee" replace />;
  return <Navigate to="/dashboard" replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/dashboard" element={<ProtectedRoute adminOnly><Dashboard /></ProtectedRoute>} />
          <Route path="/attendance" element={<PermRoute tracker="attendance"><AttendancePage /></PermRoute>} />
          <Route path="/assets" element={<PermRoute tracker="assets"><AssetPage /></PermRoute>} />
          <Route path="/clients" element={<PermRoute tracker="clients"><ClientPage /></PermRoute>} />
          <Route path="/deliverables" element={<PermRoute tracker="deliverables"><DeliverablePage /></PermRoute>} />
          <Route path="/clients/:clientId/master" element={<PermRoute tracker="clients"><MasterSheetPage /></PermRoute>} />
          <Route path="/shoots" element={<PermRoute tracker="shoots"><ShootPage /></PermRoute>} />
          <Route path="/production" element={<PermRoute tracker="production"><ProductionPage /></PermRoute>} />
          <Route path="/production/cast" element={<ProtectedRoute><ProductionCastPage /></ProtectedRoute>} />
          <Route path="/execution" element={<PermRoute tracker="execution"><ExecutionBoardPage /></PermRoute>} />
          <Route path="/performance" element={<PermRoute tracker="performance"><PerformanceBoardPage /></PermRoute>} />
          <Route path="/business-dev" element={<PermRoute tracker="business_dev"><BusinessDevPage /></PermRoute>} />
          <Route path="/payroll" element={<ProtectedRoute adminOnly><PayrollPage /></ProtectedRoute>} />
          <Route path="/approvals" element={<PermRoute tracker="approvals"><ApprovalsPage /></PermRoute>} />
          <Route path="/talent" element={<PermRoute tracker="talent"><TalentPage /></PermRoute>} />
          <Route path="/invoices" element={<ProtectedRoute adminOnly><InvoicesPage /></ProtectedRoute>} />
          <Route path="/finance" element={<ProtectedRoute adminOnly><FinancePage /></ProtectedRoute>} />
          <Route path="/punch-tracker" element={<ProtectedRoute adminOnly><PunchTrackerPage /></ProtectedRoute>} />
          <Route path="/employees" element={<ProtectedRoute adminOnly><EmployeeTrackerPage /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute adminOnly><AnalyticsPage /></ProtectedRoute>} />
          <Route path="/audit-logs" element={<ProtectedRoute adminOnly><AuditLogsPage /></ProtectedRoute>} />
          <Route path="/daily-brief" element={<ProtectedRoute adminOnly><DailyBriefPage /></ProtectedRoute>} />
          <Route path="/employee" element={<ProtectedRoute><EmployeeDashboard /></ProtectedRoute>} />
          <Route path="/" element={<RoleRedirect />} />
          <Route path="*" element={<RoleRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
